import { useState } from 'react';
import { useGameStore } from '../../store/gameSlice';
import { useLoadoutStore } from '../../store/loadoutSlice';
import { closeInterface } from '../../services/InterfaceService';
import { dungeonService } from '../../services/DungeonService';
import { PlayerEntity } from '../../entities/PlayerEntity';
import * as TrinketService from '../../services/TrinketService';
import { netService } from '../../services/NetService';
import { makePlayerLoadout } from '../../net/loadoutSync';
import { captureLoadoutSnapshot } from '../../services/LoadoutSourceService';
import { getClientToken } from '../../net/reconnect';
import { SPELL_REGISTRY } from '../../data/spells.config';
import { useLoadoutBoardData } from '../../hooks/useLoadoutBoardData';
import { LoadoutBoard, type BoardRef } from './LoadoutBoard';
import { ConfirmPopup } from './ConfirmPopup';
import { NineSliceFrame } from './NineSliceFrame';
import { Icon } from './Icon';
import { KeyDisplay } from './KeyDisplay';
import './SpellEquipScreen.scss';

/**
 * In-run screen opened when a grimoire unlocks a new spell: only spell slots
 * are selectable, to equip the spell (replacing the slot's current spell).
 * Confirmation popup on each slot pick and on exit. Reuses LoadoutBoard
 * (spell-only navigation) + the generic ConfirmPopup.
 */
export const SpellEquipScreen = () => {
  const spellId = useGameStore((s) => s.spellEquipPendingId);
  const setSpellEquipPendingId = useGameStore((s) => s.setSpellEquipPendingId);
  const equippedSlots = useLoadoutStore((s) => s.equipped.slots);
  const { slots, sockets, getCard } = useLoadoutBoardData();

  const [pendingSlot, setPendingSlot] = useState<number | null>(null);
  const [confirmingExit, setConfirmingExit] = useState(false);

  if (!spellId) {
    closeInterface();
    return null;
  }

  const spell = SPELL_REGISTRY[spellId];
  const spellName = spell?.name ?? spellId;
  const popupOpen = pendingSlot !== null || confirmingExit;

  const clearAndClose = () => {
    setSpellEquipPendingId(null);
    closeInterface();
  };

  const onActivate = (ref: BoardRef): boolean => {
    if (ref.kind !== 'spell') {
      return false;
    }
    setPendingSlot(ref.index);
    return true;
  };

  const currentSpellId =
    pendingSlot !== null ? equippedSlots[pendingSlot]?.spellId : null;
  const currentSpellName = currentSpellId
    ? (SPELL_REGISTRY[currentSpellId]?.name ?? currentSpellId)
    : null;

  return (
    <div className="spell-equip-overlay">
      <NineSliceFrame className="spell-equip-frame">
        <div className="spell-equip-screen">
          <h1 className="spell-equip-title">Équiper le sort</h1>
          <div className="spell-equip-spell">
            {spell?.icon && <Icon sprite={spell.icon} size={28} />}
            <span className="spell-equip-spell-name">{spellName}</span>
          </div>
          <LoadoutBoard
            active={!popupOpen}
            slots={slots}
            sockets={sockets}
            getCard={getCard}
            isNavigable={(ref) => ref.kind === 'spell'}
            onActivate={onActivate}
            onCancel={() => setConfirmingExit(true)}
          />
          <div className="spell-equip-hint">
            <KeyDisplay context="interface" action="CONFIRM" size="sm" />{' '}
            choisir un slot &nbsp;·&nbsp;{' '}
            <KeyDisplay context="interface" action="CANCEL" size="sm" /> quitter
          </div>
        </div>
      </NineSliceFrame>

      {pendingSlot !== null && (
        <ConfirmPopup
          message={
            currentSpellName
              ? `Remplacer ${currentSpellName} par ${spellName} ?`
              : `Équiper ${spellName} sur ce slot ?`
          }
          confirmLabel="Équiper"
          hideButtons
          onConfirm={() => {
            TrinketService.setSpellInSlot(pendingSlot, spellId);
            const player = dungeonService.getPlayer();
            if (player instanceof PlayerEntity) {
              player.syncSlotsFromLoadout();
            }
            if (netService.isClient()) {
              netService.session?.broadcast(
                'reliable',
                makePlayerLoadout(captureLoadoutSnapshot(), getClientToken()),
              );
            }
            clearAndClose();
          }}
          onCancel={() => setPendingSlot(null)}
        />
      )}

      {confirmingExit && (
        <ConfirmPopup
          message="Quitter sans équiper ? Le sort reste débloqué mais non équipé."
          confirmLabel="Quitter"
          cancelLabel="Retour"
          hideButtons
          onConfirm={clearAndClose}
          onCancel={() => setConfirmingExit(false)}
        />
      )}
    </div>
  );
};
